import { CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

interface PracticalInfoProps {
  target_audience: string[];
  prerequisites: string[];
  min_participants: number;
  max_participants: number;
  mode: 'presentiel' | 'distanciel' | 'hybride';
  accessibility: string;
}

export function PracticalInfo({
  target_audience,
  prerequisites,
  min_participants,
  max_participants,
  mode,
  accessibility,
}: PracticalInfoProps) {
  const { t, i18n } = useTranslation('formations');
  const { locale } = useParams<{ locale: string }>();
  const currentLocale = locale || i18n.language || 'fr';

  return (
    <section className="py-24 bg-transparent">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-heading font-extrabold text-aspexit-black mb-4 text-center">
            {t('practicalInfo.title')}
          </h2>
          <p className="text-center text-aspexit-gray mb-16 max-w-2xl mx-auto">
            {t('practicalInfo.subtitle')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Public ciblé */}
            <div className="bg-gradient-to-br from-[#F06C6E]/5 to-white rounded-2xl p-8 border-2 border-[#F06C6E]/20 hover:border-[#F06C6E]/40 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 mx-auto mb-6">
                <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  {/* Group of people */}
                  <circle cx="50" cy="35" r="12" fill="#F06C6E" opacity="0.8"/>
                  <path d="M 50 47 Q 50 55 40 60 Q 35 62 35 70 L 65 70 Q 65 62 60 60 Q 50 55 50 47 Z" fill="#F06C6E" opacity="0.6"/>
                  <circle cx="30" cy="40" r="9" fill="#F06C6E" opacity="0.6"/>
                  <path d="M 30 49 Q 30 55 23 58 Q 20 60 20 65 L 40 65" fill="#F06C6E" opacity="0.4"/>
                  <circle cx="70" cy="40" r="9" fill="#F06C6E" opacity="0.6"/>
                  <path d="M 70 49 Q 70 55 77 58 Q 80 60 80 65 L 60 65" fill="#F06C6E" opacity="0.4"/>
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                {t('practicalInfo.targetAudience.title')}
              </h3>
              <p className="text-sm text-aspexit-gray mb-4 text-center">
                {t('practicalInfo.targetAudience.subtitle')}
              </p>
              <ul className="space-y-2 text-sm text-aspexit-gray">
                {target_audience.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#F06C6E] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prérequis techniques */}
            <div className="bg-gradient-to-br from-[#0D974E]/5 to-white rounded-2xl p-8 border-2 border-[#0D974E]/20 hover:border-[#0D974E]/40 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 mx-auto mb-6">
                <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  {/* Laptop */}
                  <rect x="20" y="30" width="60" height="40" rx="3" fill="none" stroke="#0D974E" strokeWidth="3"/>
                  <rect x="20" y="30" width="60" height="35" rx="3" fill="#0D974E" opacity="0.1"/>
                  <line x1="35" y1="40" x2="55" y2="40" stroke="#0D974E" strokeWidth="2" opacity="0.5"/>
                  <line x1="35" y1="48" x2="65" y2="48" stroke="#0D974E" strokeWidth="2" opacity="0.5"/>
                  <line x1="35" y1="56" x2="60" y2="56" stroke="#0D974E" strokeWidth="2" opacity="0.5"/>
                  <path d="M 15 70 L 25 75 L 75 75 L 85 70 Z" fill="#0D974E" opacity="0.3"/>
                  <circle cx="25" cy="33" r="1.5" fill="#0D974E"/>
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                {t('practicalInfo.prerequisites.title')}
              </h3>
              <p className="text-sm text-aspexit-gray mb-4 text-center">
                {t('practicalInfo.prerequisites.subtitle')}
              </p>
              <ul className="space-y-2 text-sm text-aspexit-gray">
                {prerequisites.map((item, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-[#0D974E] flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Modalités */}
            <div className="bg-gradient-to-br from-[#FF9500]/5 to-white rounded-2xl p-8 border-2 border-[#FF9500]/20 hover:border-[#FF9500]/40 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 mx-auto mb-6">
                <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  {/* Location pin and calendar */}
                  <path d="M 50 25 Q 60 25 60 35 Q 60 45 50 60 Q 40 45 40 35 Q 40 25 50 25 Z" fill="#FF9500" opacity="0.6"/>
                  <circle cx="50" cy="35" r="5" fill="white"/>
                  <rect x="25" y="55" width="25" height="25" rx="2" fill="none" stroke="#FF9500" strokeWidth="2.5" opacity="0.8"/>
                  <line x1="25" y1="62" x2="50" y2="62" stroke="#FF9500" strokeWidth="2.5" opacity="0.8"/>
                  <rect x="30" y="67" width="4" height="4" fill="#FF9500" opacity="0.6"/>
                  <rect x="38" y="67" width="4" height="4" fill="#FF9500" opacity="0.6"/>
                  <rect x="30" y="73" width="4" height="4" fill="#FF9500" opacity="0.6"/>
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                {t('practicalInfo.modalities.title')}
              </h3>
              <ul className="space-y-2 text-sm text-aspexit-gray">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                  <span><strong>{t('practicalInfo.modalities.format')}</strong> {t(`modes.${mode === 'presentiel' ? 'presentiellIntra' : mode === 'distanciel' ? 'distancielInter' : 'hybrideDesc'}`)}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                  <span><strong>{t('practicalInfo.modalities.group')}</strong> {t('practicalInfo.modalities.groupRange', { min: min_participants, max: max_participants })}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                  <span><strong>{t('practicalInfo.modalities.registration')}</strong> {t('practicalInfo.modalities.registrationDelay')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#FF9500] flex-shrink-0 mt-0.5" />
                  <span><strong>{t('practicalInfo.modalities.delay')}</strong> {t('practicalInfo.modalities.delayInfo')}</span>
                </li>
              </ul>
            </div>

            {/* Accessibilité */}
            <div className="bg-gradient-to-br from-[#6C5CE7]/5 to-white rounded-2xl p-8 border-2 border-[#6C5CE7]/20 hover:border-[#6C5CE7]/40 hover:shadow-xl transition-all duration-300">
              <div className="w-20 h-20 mx-auto mb-6">
                <svg viewBox="0 0 100 100" className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                  {/* Accessibility icon */}
                  <circle cx="50" cy="50" r="35" fill="none" stroke="#6C5CE7" strokeWidth="3"/>
                  <circle cx="50" cy="35" r="6" fill="#6C5CE7"/>
                  <path d="M 35 50 L 50 50 L 50 70" stroke="#6C5CE7" strokeWidth="4" strokeLinecap="round"/>
                  <path d="M 50 50 L 65 50" stroke="#6C5CE7" strokeWidth="4" strokeLinecap="round"/>
                  <path d="M 43 70 Q 43 75 38 78" stroke="#6C5CE7" strokeWidth="4" strokeLinecap="round" fill="none"/>
                  <path d="M 57 70 Q 57 75 62 78" stroke="#6C5CE7" strokeWidth="4" strokeLinecap="round" fill="none"/>
                  <circle cx="38" cy="78" r="3" fill="#6C5CE7"/>
                  <circle cx="62" cy="78" r="3" fill="#6C5CE7"/>
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-aspexit-black mb-4 text-center">
                {t('practicalInfo.accessibility.title')}
              </h3>
              <ul className="space-y-2 text-sm text-aspexit-gray">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                  <span>{accessibility}</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>{t('practicalInfo.accessibility.referent')}</strong>{' '}
                    <a href={`/${currentLocale}/contact`} className="text-[#6C5CE7] hover:underline font-semibold">
                      {t('practicalInfo.accessibility.contact')}
                    </a>
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#6C5CE7] flex-shrink-0 mt-0.5" />
                  <span>{t('practicalInfo.accessibility.adaptations')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
